/**
 * SASADM-0170 (saas-auth SASATH-0387 §3 / §3A) — the tenant's PUBLIC apps and the build folder
 * each is served from: set a custom build folder, revert to the default, or turn the public app
 * off/on for this tenant. SUPER_ADMIN only (the parent renders it for super-admins; a 403 hides
 * it). The rows are loaded by the parent (`TenantHostnames`), which also marks disabled apps'
 * URLs; after every write the parent re-fetches (`onChanged`).
 *
 * Every write deploys routing. This panel is the one place that reports every deploy status
 * (DEPLOYED / QUEUED / SKIPPED as toasts); REJECTED / FAILED are a banner, never a success.
 *
 * SASADM-0171 (SASATH-0389) — each row also carries an offline page: the folder served while the
 * app is off (set / change / clear), through the same folder dialog in `offline` mode.
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import { isDeployProblem } from '@/api/routingDeploy';
import type { AppOverrideDto, RoutingDeployOutcome } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { RoutingDeployBanner } from '@/components/RoutingDeployBanner';
import { DisabledChip } from '@/components/DisabledChip';
import { DISABLED_TEXT_SX } from '@/components/disabledStyle';

export type AppOverridesLoadStatus = 'loading' | 'ok' | 'forbidden' | 'unavailable' | 'error';

export interface AppBuildsPanelProps {
  tenantId: number;
  tenantSlug: string;
  tenantName: string;
  rows: AppOverrideDto[];
  loadStatus: AppOverridesLoadStatus;
  /** The parent re-fetches the override rows (and the hostnames they mark). */
  onChanged: () => void;
  deployTenant: () => Promise<boolean>;
  tenantDeploy: { disabled: boolean; busy: boolean };
}

type Write = 'save' | 'revert' | 'off' | 'on' | 'offlineSave' | 'offlineClear';

/** SASADM-0171 — the folder dialog sets either a custom build or the offline page (SASATH-0389). */
type FolderMode = 'build' | 'offline';

const MONO = { fontFamily: 'monospace', wordBreak: 'break-all' } as const;

export function AppBuildsPanel({
  tenantId,
  tenantSlug,
  tenantName,
  rows,
  loadStatus,
  onChanged,
  deployTenant,
  tenantDeploy,
}: AppBuildsPanelProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [deployFailure, setDeployFailure] = useState<RoutingDeployOutcome | null>(null);
  // The row whose Set dialog is open, the dialog's mode, and its draft.
  const [editRow, setEditRow] = useState<AppOverrideDto | null>(null);
  const [mode, setMode] = useState<FolderMode>('build');
  const [folder, setFolder] = useState('');
  const [ack, setAck] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [revertRow, setRevertRow] = useState<AppOverrideDto | null>(null);
  const [disableRow, setDisableRow] = useState<AppOverrideDto | null>(null);
  const [offlineClearRow, setOfflineClearRow] = useState<AppOverrideDto | null>(null);
  const [pending, setPending] = useState<Write | null>(null);
  // Guards a second submit before the disabled state re-renders (FRM-014).
  const inFlight = useRef(false);
  const busy = pending !== null;

  if (loadStatus === 'forbidden') return null;

  function permissionText() {
    return t('errors.insufficientPermissions', "You don't have permission for this action.");
  }

  function errorText(err: ApiError): string {
    if (err.status === 403) return permissionText();
    if (err.code === 'VALIDATION_FAILED') {
      return err.details.message ?? t('errors.codes.VALIDATION_FAILED', err.code);
    }
    return t(`errors.codes.${err.code}`, err.details.message ?? err.code);
  }

  // The handoff's per-status table. A failure is the banner, never a success toast.
  function report(
    outcome: RoutingDeployOutcome | null | undefined,
    write: Write,
    app: string,
    affiliate = false,
  ) {
    // Read before the guard: its false branch narrows `outcome` to null | undefined.
    const status = outcome?.status;
    const message = outcome?.message ?? '';
    if (isDeployProblem(outcome)) {
      setDeployFailure(outcome);
      return;
    }
    setDeployFailure(null);
    if (status === 'QUEUED') {
      snack.info(t('appBuilds.queued', 'Saved. Routing deploy queued.'));
    } else if (status === 'SKIPPED') {
      snack.info(t('appBuilds.skipped', 'Saved. Routing deploy skipped: {{message}}', { message }));
    } else {
      snack.success(successText(write, status === 'DEPLOYED', app, affiliate));
    }
  }

  function successText(write: Write, deployed: boolean, app: string, affiliate: boolean): string {
    switch (write) {
      case 'save':
        return deployed
          ? t('appBuilds.savedDeployed', 'Custom build set. Routing deployed.')
          : t('appBuilds.saved', 'Saved.');
      case 'revert':
        return deployed
          ? t('appBuilds.revertedDeployed', 'Reverted to the default build. Routing deployed.')
          : t('appBuilds.reverted', 'Reverted to the default build.');
      case 'off':
        if (affiliate) {
          return deployed
            ? t(
                'appBuilds.affiliateDisabledDeployed',
                '{{app}} is off for this tenant. Routing deployed.',
                { app },
              )
            : t('appBuilds.affiliateDisabled', '{{app}} is off for this tenant.', { app });
        }
        return deployed
          ? t('appBuilds.disabledDeployed', '{{app}} is off for this tenant. Routing deployed.', {
              app,
            })
          : t('appBuilds.disabled', '{{app}} is off for this tenant.', { app });
      case 'on':
        if (affiliate) {
          return deployed
            ? t(
                'appBuilds.affiliateEnabledDeployed',
                '{{app}} is enabled for this tenant. Routing deployed.',
                { app },
              )
            : t('appBuilds.affiliateEnabled', '{{app}} is enabled for this tenant.', { app });
        }
        return deployed
          ? t('appBuilds.enabledDeployed', '{{app}} is back on. Routing deployed.', { app })
          : t('appBuilds.enabled', '{{app}} is back on.', { app });
      case 'offlineSave':
        return deployed
          ? t('appBuilds.offlineSavedDeployed', 'Offline page set. Routing deployed.')
          : t('appBuilds.offlineSaved', 'Offline page set.');
      case 'offlineClear':
        return deployed
          ? t('appBuilds.offlineClearedDeployed', 'Offline page cleared. Routing deployed.')
          : t('appBuilds.offlineCleared', 'Offline page cleared.');
    }
  }

  function openSet(row: AppOverrideDto, folderMode: FolderMode = 'build') {
    setEditRow(row);
    setMode(folderMode);
    setFolder(
      folderMode === 'offline'
        ? (row.offlineDocroot ?? `C:/caddy/htdocs/offline-${tenantSlug}`)
        : (row.overrideDocroot ?? `${row.defaultDocroot}-${tenantSlug}`),
    );
    // FRM-007 — the acknowledgement starts unchecked on every open.
    setAck(false);
    setFieldError(null);
    setDialogError(null);
  }

  async function handleSave() {
    if (!editRow || inFlight.current) return;
    const value = folder.trim();
    if (!value || !ack) return;
    const write: Write = mode === 'offline' ? 'offlineSave' : 'save';
    inFlight.current = true;
    setPending(write);
    setFieldError(null);
    setDialogError(null);
    try {
      const res =
        mode === 'offline'
          ? await apiAdmin.appOverrides.setOfflinePage(tenantId, editRow.appId, value)
          : await apiAdmin.appOverrides.set(tenantId, editRow.appId, value);
      setEditRow(null);
      onChanged();
      report(res.routingDeploy, write, editRow.appName);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'VALIDATION_FAILED') setFieldError(errorText(err));
        else setDialogError(errorText(err));
      }
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  }

  async function handleClearOffline() {
    const row = offlineClearRow;
    if (!row || inFlight.current) return;
    inFlight.current = true;
    setPending('offlineClear');
    try {
      const outcome = await apiAdmin.appOverrides.clearOfflinePage(tenantId, row.appId);
      setOfflineClearRow(null);
      onChanged();
      report(outcome, 'offlineClear', row.appName);
    } catch (err) {
      setOfflineClearRow(null);
      if (err instanceof ApiError) snack.error(errorText(err));
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  }

  async function handleRevert() {
    const row = revertRow;
    if (!row || inFlight.current) return;
    inFlight.current = true;
    setPending('revert');
    try {
      const outcome = await apiAdmin.appOverrides.clear(tenantId, row.appId);
      setRevertRow(null);
      onChanged();
      report(outcome, 'revert', row.appName);
    } catch (err) {
      setRevertRow(null);
      if (err instanceof ApiError) snack.error(errorText(err));
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  }

  async function handleSetEnabled(row: AppOverrideDto, enabled: boolean) {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(enabled ? 'on' : 'off');
    try {
      const res = await apiAdmin.appOverrides.setEnabled(tenantId, row.appId, enabled);
      setDisableRow(null);
      onChanged();
      report(res.routingDeploy, enabled ? 'on' : 'off', row.appName, row.role === 'AFFILIATE');
    } catch (err) {
      setDisableRow(null);
      if (err instanceof ApiError) snack.error(errorText(err));
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  }

  async function handleDeployFromBanner() {
    const ok = await deployTenant();
    if (ok) setDeployFailure(null);
  }

  function renderRow(row: AppOverrideDto) {
    const off = !row.enabled;
    // SASADM-0172 (SASATH-0388 §3) — an opt-in AFFILIATE row: Enabled switch only (default off);
    // no custom build and no offline page, which stay PUBLIC-only.
    const affiliate = row.role === 'AFFILIATE';
    const overridden = !affiliate && row.overrideDocroot != null;
    const hasOffline = !affiliate && row.offlineDocroot != null;
    return (
      <TableRow key={row.appId}>
        <TableCell>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
          >
            <Typography variant="body2" sx={off ? DISABLED_TEXT_SX : undefined}>
              {row.appName}
            </Typography>
            {off && <DisabledChip />}
            <Chip
              size="small"
              variant="outlined"
              label={t(`products.apps.roles.${row.role}.label`, row.role)}
            />
          </Stack>
        </TableCell>
        <TableCell>
          <Switch
            checked={row.enabled}
            disabled={busy}
            onChange={(e) => {
              if (e.target.checked) void handleSetEnabled(row, true);
              else setDisableRow(row);
            }}
            slotProps={{
              input: {
                'aria-label': t('appBuilds.enabledAria', 'Enabled: {{app}}', { app: row.appName }),
              },
            }}
          />
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {overridden ? (
              <>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, ...MONO, ...(off ? DISABLED_TEXT_SX : {}) }}
                >
                  {row.overrideDocroot}
                </Typography>
                <Chip size="small" label={t('appBuilds.custom', 'Custom')} />
              </>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', ...MONO, ...(off ? DISABLED_TEXT_SX : {}) }}
                >
                  {row.defaultDocroot}
                </Typography>
                {!affiliate && (
                  <Chip size="small" variant="outlined" label={t('appBuilds.default', 'Default')} />
                )}
              </>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          {hasOffline ? (
            <>
              <Typography variant="body2" sx={MONO}>
                {row.offlineDocroot}
              </Typography>
              {row.enabled && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {t('appBuilds.offlineIdle', 'Used only while the app is off')}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              —
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">
          {!affiliate && (
            <Stack direction="row" sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
              <Button size="small" onClick={() => openSet(row)} disabled={busy}>
                {overridden
                  ? t('appBuilds.change', 'Change')
                  : t('appBuilds.set', 'Set custom build')}
              </Button>
              {overridden && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => setRevertRow(row)}
                  disabled={busy}
                >
                  {t('appBuilds.revert', 'Revert to default')}
                </Button>
              )}
              <Button size="small" onClick={() => openSet(row, 'offline')} disabled={busy}>
                {hasOffline
                  ? t('appBuilds.offlineChange', 'Change offline page')
                  : t('appBuilds.offlineSet', 'Set offline page')}
              </Button>
              {hasOffline && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => setOfflineClearRow(row)}
                  disabled={busy}
                >
                  {t('appBuilds.offlineClear', 'Clear offline page')}
                </Button>
              )}
            </Stack>
          )}
        </TableCell>
      </TableRow>
    );
  }

  function renderBody() {
    if (loadStatus === 'loading') {
      return (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <CircularProgress size={16} />
          <Typography sx={{ color: 'text.secondary' }}>{t('common.loading', 'Loading')}</Typography>
        </Stack>
      );
    }
    if (loadStatus === 'unavailable') {
      return (
        <Alert severity="info">
          {t(
            'appBuilds.unavailable',
            "Custom builds aren't available yet (saas-auth SASATH-0387 not deployed).",
          )}
        </Alert>
      );
    }
    if (loadStatus === 'error') {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onChanged}>
              {t('common.retry', 'Retry')}
            </Button>
          }
        >
          {t('appBuilds.loadError', "Couldn't load app builds.")}
        </Alert>
      );
    }
    if (rows.length === 0) {
      return (
        <Typography sx={{ color: 'text.secondary' }}>
          {t('appBuilds.empty', 'This product has no public app.')}
        </Typography>
      );
    }
    return (
      <Table size="small" aria-label={t('appBuilds.title', 'App builds')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('appBuilds.colApp', 'App')}</TableCell>
            <TableCell>{t('appBuilds.colEnabled', 'Enabled')}</TableCell>
            <TableCell>{t('appBuilds.colServingFrom', 'Serving from')}</TableCell>
            <TableCell>{t('appBuilds.colOfflinePage', 'Offline page')}</TableCell>
            <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{rows.map(renderRow)}</TableBody>
      </Table>
    );
  }

  const saving = pending === 'save' || pending === 'offlineSave';
  const revertFolder = revertRow?.defaultDocroot ?? '';

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h3" component="h3" sx={{ mb: 0.5 }}>
        {t('appBuilds.title', 'App builds')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t(
          'appBuilds.help',
          "Serve this tenant a customized build of its public app, or turn the public app off for this tenant. It is the same app — sign-in, roles, API and hosts don't change; only what Caddy serves for this tenant does.",
        )}
      </Typography>
      {deployFailure && (
        <RoutingDeployBanner
          outcome={deployFailure}
          currentTenantId={tenantId}
          onClose={() => setDeployFailure(null)}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={tenantDeploy.disabled}
              startIcon={tenantDeploy.busy ? <CircularProgress size={16} color="inherit" /> : null}
              onClick={() => void handleDeployFromBanner()}
            >
              {t('routingDeploy.deployTenant', "Deploy this tenant's routing")}
            </Button>
          }
        />
      )}
      <Box>{renderBody()}</Box>
      <FolderDialog
        open={editRow !== null}
        mode={mode}
        appName={editRow?.appName ?? ''}
        folder={folder}
        onFolderChange={(value) => {
          setFolder(value);
          setFieldError(null);
        }}
        ack={ack}
        onAckChange={setAck}
        fieldError={fieldError}
        dialogError={dialogError}
        saving={saving}
        onCancel={() => setEditRow(null)}
        onSave={() => void handleSave()}
      />
      <ConfirmDialog
        open={revertRow !== null}
        title={t('appBuilds.revertTitle', 'Revert {{app}} to the default build?', {
          app: revertRow?.appName ?? '',
        })}
        body={
          revertRow && !revertRow.enabled
            ? `${t('appBuilds.revertBody', 'This tenant will be served {{folder}} again.', {
                folder: revertFolder,
              })} ${t('appBuilds.revertStillOff', 'This does not turn the app back on.')}`
            : t('appBuilds.revertBody', 'This tenant will be served {{folder}} again.', {
                folder: revertFolder,
              })
        }
        severity="warning"
        confirmLabel={t('appBuilds.revert', 'Revert to default')}
        busy={pending === 'revert'}
        onConfirm={() => void handleRevert()}
        onCancel={() => setRevertRow(null)}
      />
      <ConfirmDialog
        open={disableRow !== null}
        title={t('appBuilds.disableTitle', 'Turn off {{app}} for {{tenant}}?', {
          app: disableRow?.appName ?? '',
          tenant: tenantName,
        })}
        body={
          disableRow?.role === 'AFFILIATE'
            ? t(
                'appBuilds.affiliateDisableBody',
                'Its affiliate host stops serving for this tenant. Customers, staff and the public app are unaffected. Turning it back on restores it.',
              )
            : t(
                'appBuilds.disableBody',
                'Its customers can\'t sign in, stay signed in, or sign up until you turn it back on. Staff, admins and affiliates are unaffected. The public site shows the offline page below, or "This site is currently unavailable."',
              )
        }
        severity="error"
        confirmLabel={t('appBuilds.disableConfirm', 'Turn off')}
        busy={pending === 'off'}
        onConfirm={() => {
          if (disableRow) void handleSetEnabled(disableRow, false);
        }}
        onCancel={() => setDisableRow(null)}
      />
      <ConfirmDialog
        open={offlineClearRow !== null}
        title={t('appBuilds.offlineClear', 'Clear offline page')}
        body={t(
          'appBuilds.offlineClearConfirm',
          "Clear the offline page? While the app is off, visitors will see a plain 'unavailable' message.",
        )}
        severity="warning"
        confirmLabel={t('appBuilds.offlineClear', 'Clear offline page')}
        busy={pending === 'offlineClear'}
        onConfirm={() => void handleClearOffline()}
        onCancel={() => setOfflineClearRow(null)}
      />
    </Paper>
  );
}

interface FolderDialogProps {
  open: boolean;
  mode: FolderMode;
  appName: string;
  folder: string;
  onFolderChange: (value: string) => void;
  ack: boolean;
  onAckChange: (checked: boolean) => void;
  fieldError: string | null;
  dialogError: string | null;
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
}

// SASADM-0171 — one folder dialog, two configurations: the custom build (SASATH-0387) and the
// offline page served while the app is off (SASATH-0389). Required acknowledgement in both.
function FolderDialog({
  open,
  mode,
  appName,
  folder,
  onFolderChange,
  ack,
  onAckChange,
  fieldError,
  dialogError,
  saving,
  onCancel,
  onSave,
}: FolderDialogProps) {
  const { t } = useCopy();
  const offline = mode === 'offline';
  const canSave = folder.trim().length > 0 && ack && !saving;
  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="app-build-dialog-title"
    >
      <DialogTitle id="app-build-dialog-title">
        {offline
          ? t('appBuilds.offlineDialogTitle', 'Offline page for {{app}}', { app: appName })
          : t('appBuilds.dialogTitle', 'Custom build for {{app}}', { app: appName })}
      </DialogTitle>
      <DialogContent>
        {dialogError && <ErrorBanner message={dialogError} />}
        {offline && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {t(
              'appBuilds.offlineHint',
              "Shown only while the app is off. Without one, visitors see a plain 'unavailable' message.",
            )}
          </Typography>
        )}
        <TextField
          required
          fullWidth
          label={
            offline
              ? t('appBuilds.offlineField', 'Offline page folder on the edge')
              : t('appBuilds.folderLabel', 'Build folder on the edge')
          }
          placeholder={
            offline ? 'C:/caddy/htdocs/offline-<tenant>' : 'C:/caddy/htdocs/<app>-<tenant>'
          }
          value={folder}
          onChange={(e) => onFolderChange(e.target.value)}
          error={fieldError !== null}
          helperText={fieldError}
          sx={{ mt: 1 }}
          slotProps={{ htmlInput: { sx: { fontFamily: 'monospace' } } }}
        />
        <FormControlLabel
          required
          sx={{ mt: 2, alignItems: 'flex-start' }}
          control={
            <Checkbox
              checked={ack}
              onChange={(e) => onAckChange(e.target.checked)}
              sx={{ pt: 0 }}
            />
          }
          label={
            offline
              ? t('appBuilds.offlineConfirm', 'Deploy the static site to this folder first.')
              : t(
                  'appBuilds.confirm',
                  "Deploy the custom build to this folder first. Until it has an index.html, this tenant's site shows 404.",
                )
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={onSave}
        >
          {t('appBuilds.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
